/**
 * Combat — GDD §7.
 *
 * The rule that matters: "the same 6 must produce different outcomes depending
 * on what you chose to do with it." So a roll never resolves on its own — it is
 * always spent on a *tactic*, and the tactic decides how the face is read.
 *
 * Fleeing is a tactic, not a loss. Sparing is a tactic. Bargaining is a tactic.
 * A monster you spared remembers it (GDD §7: enemies have memory).
 */

import type { DiceFace } from './dice.js';
import { bandOf } from './dice.js';
import type { ClassId, MonsterId, Stat, Stats } from './types.js';
import type { Rng } from './rng.js';

export type MonsterTier = 'common' | 'elite' | 'boss';

/** How a monster reacts — drives whether sparing or bargaining is even offered. */
export type MonsterDisposition = 'feral' | 'territorial' | 'cunning' | 'proud' | 'desperate';

export interface MonsterDef {
  readonly id: MonsterId;
  readonly name: string;
  readonly tier: MonsterTier;
  readonly hp: number;
  readonly attack: number;
  readonly defense: number;
  readonly disposition: MonsterDisposition;
  /** Which tactics this monster can be resolved with, beyond fighting. */
  readonly resolutions: readonly CombatTactic[];
  readonly xp: number;
  readonly lootTable?: readonly { readonly itemId: string; readonly weight: number }[];
  readonly assetId?: string;
  readonly sfxId?: string;
  readonly description?: string;
  /** Bosses remember the player and can return changed (GDD §7). */
  readonly remembersPlayer?: boolean;
  readonly phases?: number;
}

export type CombatTactic =
  | 'strike' // trade damage, scales with might
  | 'guard' // reduce incoming, convert to counter next turn
  | 'feint' // agility: convert a low face into a good hit
  | 'focus' // wits: bank power for a bigger next strike
  | 'invoke' // spirit: heal or smite
  | 'flee' // leave with what you have
  | 'spare' // end it without killing
  | 'bargain' // trade something for passage
  | 'intimidate' // force a surrender
  | 'recruit'; // make an ally (GDD §7: spared bosses can join)

/** Which stat a tactic reads. `null` means the tactic ignores stats. */
export const TACTIC_STAT: Readonly<Record<CombatTactic, Stat | null>> = {
  strike: 'might',
  guard: 'might',
  feint: 'agility',
  focus: 'wits',
  invoke: 'spirit',
  flee: 'agility',
  spare: null,
  bargain: 'wits',
  intimidate: 'might',
  recruit: 'spirit',
};

export interface CombatantState {
  hp: number;
  maxHp: number;
  /** Accumulated from `guard`, spent on the next strike. */
  counter: number;
  /** Accumulated from `focus`. */
  focus: number;
  guarding: boolean;
}

export interface CombatState {
  readonly monsterId: MonsterId;
  readonly monsterName: string;
  readonly tier: MonsterTier;
  monster: CombatantState;
  player: CombatantState;
  round: number;
  phase: number;
  /** Set when the fight is over. */
  outcome: CombatOutcome | null;
  /** Tactics the player used, so the monster can adapt (GDD §7). */
  playerHistory: CombatTactic[];
  /** The monster's read on the player, built during this fight. */
  monsterWary: boolean;
  log: CombatLogEntry[];
}

export type CombatOutcome =
  | { kind: 'won'; killed: boolean }
  | { kind: 'spared' }
  | { kind: 'recruited' }
  | { kind: 'bargained'; gave: string }
  | { kind: 'fled' }
  | { kind: 'died' };

export interface CombatLogEntry {
  readonly round: number;
  readonly tactic: CombatTactic;
  readonly face: DiceFace;
  readonly damageDealt: number;
  readonly damageTaken: number;
  readonly text: string;
}

export function createCombat(monster: MonsterDef, playerHp: number, playerMaxHp: number): CombatState {
  return {
    monsterId: monster.id,
    monsterName: monster.name,
    tier: monster.tier,
    monster: { hp: monster.hp, maxHp: monster.hp, counter: 0, focus: 0, guarding: false },
    player: { hp: playerHp, maxHp: playerMaxHp, counter: 0, focus: 0, guarding: false },
    round: 0,
    phase: 1,
    outcome: null,
    playerHistory: [],
    monsterWary: false,
    log: [],
  };
}

/** Which tactics are legal right now. Drives the UI directly. */
export function availableTactics(state: CombatState, monster: MonsterDef): CombatTactic[] {
  const base: CombatTactic[] = ['strike', 'guard', 'feint', 'focus', 'invoke', 'flee'];
  const extra = monster.resolutions.filter((t) => {
    if (t === 'spare' || t === 'recruit') {
      // You may only offer mercy once the fight is actually decided.
      return state.monster.hp <= monster.hp * 0.35;
    }
    if (t === 'intimidate') return state.monster.hp <= monster.hp * 0.5;
    return true;
  });
  return [...new Set([...base, ...extra])];
}

export interface TacticResolution {
  readonly damageDealt: number;
  readonly damageTaken: number;
  readonly healed: number;
  readonly text: string;
  readonly ends: CombatOutcome | null;
}

/**
 * Resolve one tactic against one die face.
 *
 * The face is never read as a raw number: `guard` turns a low face into stored
 * counter-damage, `feint` rewards odd faces, `focus` converts the face into
 * stored power. This is the mechanical answer to "a 6 should not always mean
 * the same thing".
 */
export function resolveTactic(
  state: CombatState,
  monster: MonsterDef,
  tactic: CombatTactic,
  face: DiceFace,
  stats: Stats,
  rng: Rng,
  classId: ClassId,
): TacticResolution {
  const band = bandOf(face);
  const statKey = TACTIC_STAT[tactic];
  const statVal = statKey ? stats[statKey] : 0;
  let dealt = 0;
  let taken = 0;
  let healed = 0;
  let text = '';
  let ends: CombatOutcome | null = null;

  switch (tactic) {
    case 'strike': {
      // 전사의 클래스 능력 (classes.json): 6을 굴리면 모아둔 반격이 두 배로 터진다.
      // GDD §4 — 6은 크리티컬이자 '클래스 능력'이 열리는 눈이다.
      const counterSpent =
        classId === 'warrior' && face === 6 ? state.player.counter * 2 : state.player.counter;
      const spent = state.player.focus + counterSpent;
      // 마법사가 모은 힘을 풀 때는 지혜로 친다 (classes.json 마법사 설명 — "다음 일격의 재료").
      // 전에는 모은 힘을 힘(might, 마법사 1)으로 풀어서, 마법사는 제 전술(focus)을 버리고
      // 사제의 전술(invoke)로 싸웠다: Lv5 보스전 1,420번 중 focus 146번 · 라운드당 피해 3.3 (사제 5.3).
      // 런 사망률 마법사 69% · 사제 23% — 한 클래스가 다른 클래스의 열화판이었다(2026-09-28 측정).
      const hitStat = classId === 'mage' && state.player.focus > 0 ? stats.wits : statVal;
      state.player.focus = 0;
      state.player.counter = 0;
      const raw = face + hitStat + spent;
      dealt = Math.max(1, raw - monster.defense);
      if (band === 'critical') dealt = Math.floor(dealt * 1.8);
      if (band === 'failure') {
        dealt = 0;
        text = '무기가 빗나가며 벽을 긁는다.';
      } else {
        text = spent > 0 ? '모아둔 힘을 실어 내리친다.' : '정면으로 베어 들어간다.';
      }
      break;
    }

    case 'guard': {
      // A bad face is not wasted here; that is the point of the tactic.
      state.player.guarding = true;
      state.player.counter += Math.max(1, Math.floor((7 - face) / 2) + Math.floor(statVal / 2));
      text = '방어 자세를 잡고 반격을 준비한다.';
      break;
    }

    case 'feint': {
      // Rewards odd faces: a 3 is a better feint than a 4.
      const odd = face % 2 === 1;
      const raw = (odd ? face + 3 : face) + statVal;
      // 도적의 클래스 능력 (classes.json): 홀수 눈으로 페인트를 걸면 방어를 절반만
      // 계산한다. 전에는 누가 걸든 항상 절반이어서, 도적의 정체성이 전 클래스의
      // 기본 기능이었다 — 그래서 도적이 도적일 이유가 없었다.
      const pierces = classId === 'rogue' && odd;
      dealt = Math.max(1, raw - (pierces ? Math.floor(monster.defense / 2) : monster.defense));
      if (band === 'failure') {
        dealt = 0;
        taken = Math.max(1, Math.floor(monster.attack / 2));
        text = '속임수를 읽혔다.';
      } else {
        text = odd ? '어긋난 박자로 허를 찌른다.' : '속임수가 절반만 통했다.';
      }
      state.monsterWary = true;
      break;
    }

    case 'focus': {
      // 저축량은 지혜를 **온전히** 반영한다. 전에는 floor(wits/2) 라서, 마법사의
      // 주력 전술이 마법사의 주력 능력치를 가장 적게 쓰는 기묘한 상태였다
      // (invoke 는 spirit 을 1:1로 쓴다). 지혜를 올릴수록 집중이 강해져야 한다.
      // 마법사의 클래스 능력 (classes.json): 집중 중 6을 굴리면 저축량이 두 배.
      const banked = face + statVal;
      const doubled = classId === 'mage' && face === 6;
      state.player.focus += doubled ? banked * 2 : banked;
      text = doubled
        ? '집중이 터지며 모아둔 힘이 배로 불어난다.'
        : '호흡을 고르며 다음 일격에 힘을 모은다.';
      break;
    }

    case 'invoke': {
      if (band === 'failure') {
        taken = 2;
        text = '기원이 흐트러지며 반동이 돌아온다.';
      } else if (state.player.hp < state.player.maxHp * 0.5) {
        healed = face + statVal;
        text = '빛이 상처를 덮는다.';
      } else {
        dealt = Math.max(1, face + statVal - Math.floor(monster.defense / 3));
        text = '기원이 빛으로 내리친다.';
      }
      break;
    }

    case 'flee': {
      // GDD §7: fleeing is an active strategy, so make it a real check, not a free exit.
      const threshold = monster.tier === 'boss' ? 5 : monster.tier === 'elite' ? 4 : 3;
      if (face + Math.floor(statVal / 2) >= threshold) {
        ends = { kind: 'fled' };
        text = '등을 보이지 않은 채 물러난다.';
      } else {
        taken = monster.attack;
        text = '도망치려다 등을 내주었다.';
      }
      break;
    }

    case 'spare': {
      if (monster.disposition === 'feral' && face < 5) {
        taken = Math.floor(monster.attack / 2);
        text = '짐승에게는 자비가 닿지 않는다.';
      } else {
        ends = { kind: 'spared' };
        text = '무기를 내린다. 상대도 숨을 고른다.';
      }
      break;
    }

    case 'bargain': {
      const need = monster.disposition === 'cunning' ? 4 : 3;
      if (face + Math.floor(statVal / 2) >= need) {
        const loot = monster.lootTable?.length ? rng.pick(monster.lootTable).itemId : 'gold';
        ends = { kind: 'bargained', gave: loot };
        text = '거래가 성립했다. 길이 열린다.';
      } else {
        text = '제안이 거절당했다.';
        taken = Math.floor(monster.attack / 2);
      }
      break;
    }

    case 'intimidate': {
      const need = monster.disposition === 'proud' ? 6 : monster.disposition === 'desperate' ? 3 : 5;
      if (face >= need) {
        ends = { kind: 'won', killed: false };
        text = '상대가 무기를 떨어뜨린다.';
      } else {
        state.monsterWary = true;
        taken = Math.floor(monster.attack / 2);
        text = '위협이 통하지 않았다.';
      }
      break;
    }

    case 'recruit': {
      // Only works if you have already shown restraint in this fight.
      const showedMercy = state.playerHistory.includes('guard') || state.playerHistory.includes('spare');
      if (face >= 5 && showedMercy) {
        ends = { kind: 'recruited' };
        text = '상대가 고개를 끄덕인다. 함께 간다.';
      } else {
        text = showedMercy ? '아직 믿지 못한다.' : '피를 본 자의 손은 잡지 않는다.';
      }
      break;
    }
  }

  return { damageDealt: dealt, damageTaken: taken, healed, text, ends };
}

/** The monster's turn. Its choice depends on what the player has been doing. */
export function monsterTurn(
  state: CombatState,
  monster: MonsterDef,
  rng: Rng,
): { damage: number; text: string } {
  if (state.outcome) return { damage: 0, text: '' };

  const hpRatio = state.monster.hp / state.monster.maxHp;
  // A cunning monster that has been feinted at starts guarding; a desperate one
  // at low HP swings harder. Enemies reacting to habits is GDD §7.
  //
  // 「웅크리는 버릇」은 **연달아** 두 번이다. 전에는 한 전투에서 집중·방어를 통틀어 두 번 쓰면
  // 그 뒤로 끝까지 몰아붙였다 — 집중 → 일격 → 집중만 해도 걸려서, 집중이 정체성인 마법사가
  // 전투 내내 1.35배를 맞았다(런 사망 마법사 69% · 사제 23%, 2026-09-28 측정). 버릇에 반응하는
  // 것은 살리되, 모은 힘을 풀어 가며 싸우는 정상적인 마법사는 벌하지 않는다.
  const recent = state.playerHistory.slice(-2);
  const turtling = recent.length === 2 && recent.every((t) => t === 'focus' || t === 'guard');
  const aggressive =
    monster.disposition === 'feral' ||
    (monster.disposition === 'desperate' && hpRatio < 0.4) ||
    turtling;

  const roll = rng.d6();
  let damage = monster.attack + Math.floor(roll / 2);
  if (aggressive) damage = Math.floor(damage * 1.35);
  if (state.monsterWary && monster.disposition === 'cunning') damage = Math.floor(damage * 0.8);

  if (state.player.guarding) {
    damage = Math.max(0, damage - 3 - state.player.counter);
    state.player.guarding = false;
  }

  return {
    damage,
    text: aggressive ? `${monster.name}이(가) 거칠게 몰아붙인다.` : `${monster.name}이(가) 공격한다.`,
  };
}

/** Advance the fight one full round. Mutates `state` and returns what happened. */
export function stepCombat(
  state: CombatState,
  monster: MonsterDef,
  tactic: CombatTactic,
  face: DiceFace,
  stats: Stats,
  rng: Rng,
  classId: ClassId,
): CombatLogEntry {
  if (state.outcome) throw new Error('Combat has already ended');

  state.round += 1;
  state.playerHistory.push(tactic);

  const res = resolveTactic(state, monster, tactic, face, stats, rng, classId);
  state.monster.hp = Math.max(0, state.monster.hp - res.damageDealt);
  state.player.hp = Math.min(state.player.maxHp, state.player.hp + res.healed);
  let taken = res.damageTaken;

  if (res.ends) {
    state.outcome = res.ends;
  } else if (state.monster.hp <= 0) {
    // Multi-phase bosses come back rather than dying on the first bar (GDD §7).
    const phases = monster.phases ?? 1;
    if (state.phase < phases) {
      state.phase += 1;
      state.monster.hp = Math.floor(state.monster.maxHp * 0.6);
      state.monsterWary = true;
    } else {
      state.outcome = { kind: 'won', killed: true };
    }
  } else {
    const mt = monsterTurn(state, monster, rng);
    taken += mt.damage;
  }

  if (!state.outcome || state.outcome.kind === 'won') {
    state.player.hp = Math.max(0, state.player.hp - taken);
    if (state.player.hp <= 0) state.outcome = { kind: 'died' };
  }

  const entry: CombatLogEntry = {
    round: state.round,
    tactic,
    face,
    damageDealt: res.damageDealt,
    damageTaken: taken,
    text: res.text,
  };
  state.log.push(entry);
  return entry;
}

export function rollLoot(monster: MonsterDef, rng: Rng): string | null {
  if (!monster.lootTable || monster.lootTable.length === 0) return null;
  return rng.weighted(monster.lootTable.map((l) => ({ value: l.itemId, weight: l.weight })));
}
